import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-hot-toast"
import { HiOutlineCurrencyRupee } from "react-icons/hi"
import { MdNavigateNext } from "react-icons/md"
import { FiAlertCircle } from "react-icons/fi"
import { useDispatch, useSelector } from "react-redux"

import {
  addCourseDetails,
  editCourseDetails,
  fetchCourseCategories,
} from "../../../../../services/operations/courseDetailsAPI"
import { setCourse, setStep } from "../../../../../slices/courseSlice"
import { COURSE_STATUS } from "../../../../../utils/constants"
import IconBtn from "../../../../common/IconBtn"
import Upload from "../Upload"
import ChipInput from "./ChipInput"
import RequirementsField from "./RequirementField"

const FieldError = ({ msg }) => (
  <p className="mt-1 flex items-center gap-1 text-xs text-pink-200">
    <FiAlertCircle size={12} /> {msg}
  </p>
)

export default function CourseInformationForm() {
  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm()
  const dispatch = useDispatch()
  const { token } = useSelector((state) => state.auth)
  const { course, editCourse } = useSelector((state) => state.course)
  const [loading, setLoading] = useState(false)
  const [courseCategories, setCourseCategories] = useState([])

  useEffect(() => {
    const getCategories = async () => {
      setLoading(true)
      const cats = await fetchCourseCategories()
      if (cats.length > 0) setCourseCategories(cats)
      setLoading(false)
    }
    if (editCourse) {
      setValue("courseTitle", course.courseName)
      setValue("courseShortDesc", course.courseDescription)
      setValue("coursePrice", course.price)
      setValue("courseTags", course.tag)
      setValue("courseBenefits", course.whatYouWillLearn)
      setValue("courseCategory", course.category)
      setValue("courseRequirements", course.instructions)
      setValue("courseImage", course.thumbnail)
    }
    getCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isFormUpdated = () => {
    const v = getValues()
    return (
      v.courseTitle !== course.courseName ||
      v.courseShortDesc !== course.courseDescription ||
      v.coursePrice !== course.price ||
      v.courseTags.toString() !== course.tag.toString() ||
      v.courseBenefits !== course.whatYouWillLearn ||
      v.courseCategory._id !== course.category._id ||
      v.courseRequirements.toString() !== course.instructions.toString() ||
      v.courseImage !== course.thumbnail
    )
  }

  const onSubmit = async (data) => {
    if (editCourse) {
      if (!isFormUpdated()) { toast.error("No changes made to the form"); return }
      const v = getValues()
      const fd = new FormData()
      fd.append("courseId", course._id)
      if (v.courseTitle !== course.courseName) fd.append("courseName", data.courseTitle)
      if (v.courseShortDesc !== course.courseDescription) fd.append("courseDescription", data.courseShortDesc)
      if (v.coursePrice !== course.price) fd.append("price", data.coursePrice)
      if (v.courseTags.toString() !== course.tag.toString()) fd.append("tag", JSON.stringify(data.courseTags))
      if (v.courseBenefits !== course.whatYouWillLearn) fd.append("whatYouWillLearn", data.courseBenefits)
      if (v.courseCategory._id !== course.category._id) fd.append("category", data.courseCategory)
      if (v.courseRequirements.toString() !== course.instructions.toString()) fd.append("instructions", JSON.stringify(data.courseRequirements))
      if (v.courseImage !== course.thumbnail) fd.append("thumbnailImage", data.courseImage)
      setLoading(true)
      const result = await editCourseDetails(fd, token)
      setLoading(false)
      if (result) { dispatch(setStep(2)); dispatch(setCourse(result)) }
      return
    }

    const fd = new FormData()
    fd.append("courseName", data.courseTitle)
    fd.append("courseDescription", data.courseShortDesc)
    fd.append("price", data.coursePrice)
    fd.append("tag", JSON.stringify(data.courseTags))
    fd.append("whatYouWillLearn", data.courseBenefits)
    fd.append("category", data.courseCategory)
    fd.append("status", COURSE_STATUS.DRAFT)
    fd.append("instructions", JSON.stringify(data.courseRequirements))
    fd.append("thumbnailImage", data.courseImage)
    setLoading(true)
    const result = await addCourseDetails(fd, token)
    if (result) { dispatch(setStep(2)); dispatch(setCourse(result)) }
    setLoading(false)
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5 rounded-2xl border border-richblack-700 bg-richblack-800 p-4 sm:p-6"
    >
      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-richblack-5" htmlFor="courseTitle">
          Course Title <sup className="text-pink-200">*</sup>
        </label>
        <input
          id="courseTitle"
          placeholder="e.g. Complete React Developer Course"
          {...register("courseTitle", { required: true })}
          className="form-style w-full"
        />
        {errors.courseTitle && <FieldError msg="Course title is required" />}
      </div>

      {/* Short Description */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-richblack-5" htmlFor="courseShortDesc">
          Short Description <sup className="text-pink-200">*</sup>
        </label>
        <textarea
          id="courseShortDesc"
          rows={3}
          placeholder="A brief overview shown on the course card (2–3 sentences)"
          {...register("courseShortDesc", { required: true })}
          className="form-style min-h-[80px] w-full resize-y"
        />
        {errors.courseShortDesc && <FieldError msg="Short description is required" />}
      </div>

      {/* Price + Category */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-richblack-5" htmlFor="coursePrice">
            Price <sup className="text-pink-200">*</sup>
          </label>
          <div className="relative">
            <input
              id="coursePrice"
              type="number"
              min="0"
              placeholder="0"
              {...register("coursePrice", {
                required: true,
                min: { value: 0, message: "Price cannot be negative" },
                valueAsNumber: true,
              })}
              className="form-style w-full !pl-10"
            />
            <HiOutlineCurrencyRupee className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-richblack-400" />
          </div>
          {errors.coursePrice && <FieldError msg={errors.coursePrice.message || "Price is required"} />}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-richblack-5" htmlFor="courseCategory">
            Category <sup className="text-pink-200">*</sup>
          </label>
          <select
            id="courseCategory"
            defaultValue=""
            {...register("courseCategory", { required: true })}
            className="form-style w-full"
          >
            <option value="" disabled>
              {loading ? "Loading…" : "Select a category"}
            </option>
            {courseCategories.map((cat) => (
              <option key={cat._id} value={cat._id}>{cat.name}</option>
            ))}
          </select>
          {errors.courseCategory && <FieldError msg="Category is required" />}
        </div>
      </div>

      {/* Tags */}
      <ChipInput
        label="Tags"
        name="courseTags"
        placeholder="Type a tag and press Enter"
        register={register}
        errors={errors}
        setValue={setValue}
        getValues={getValues}
      />

      {/* Thumbnail */}
      <Upload
        name="courseImage"
        label="Course Thumbnail"
        register={register}
        setValue={setValue}
        errors={errors}
        editData={editCourse ? course?.thumbnail : null}
      />

      {/* What You'll Learn */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-richblack-5" htmlFor="courseBenefits">
          What Students Will Learn <sup className="text-pink-200">*</sup>
        </label>
        <textarea
          id="courseBenefits"
          rows={4}
          placeholder="List the key skills and knowledge students gain (one per line)"
          {...register("courseBenefits", { required: true })}
          className="form-style min-h-[100px] w-full resize-y"
        />
        {errors.courseBenefits && <FieldError msg="Please describe the course benefits" />}
      </div>

      {/* Requirements */}
      <RequirementsField
        name="courseRequirements"
        label="Requirements / Prerequisites"
        register={register}
        setValue={setValue}
        errors={errors}
        getValues={getValues}
      />

      {/* Actions */}
      <div className="flex flex-col-reverse gap-3 border-t border-richblack-700 pt-4 sm:flex-row sm:items-center sm:justify-end">
        {editCourse && (
          <button
            type="button"
            disabled={loading}
            onClick={() => dispatch(setStep(2))}
            className="w-full rounded-xl border border-richblack-600 py-2.5 text-center text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 disabled:opacity-50 sm:w-auto sm:px-5"
          >
            Skip — Continue Without Saving
          </button>
        )}
        <IconBtn
          disabled={loading}
          text={!editCourse ? "Save & Continue" : "Save Changes"}
        >
          <MdNavigateNext />
        </IconBtn>
      </div>
    </form>
  )
}
